import React, { useEffect, useState } from "react";
import { compareDesc, format } from "date-fns";
import { useFormContext } from "react-hook-form";
import { Dialog, type FilterOptionsState, Form, Toast } from "@sscs/ui";
import { DATE_FIELD_FORMAT } from "@sscs/utils";
import {
  NOTIFY_ON_COMPLETION_LABEL,
  PURL_BLACKLIST_CHARS,
  PURL_SAFE_PATTERN,
} from "Constants";
import { useIsAiFeatureEnabled } from "Hooks";
import {
  useGenerateSoftwareInfoMutation,
  useSoftwareInfoChoicesQuery,
} from "Services";
import {
  GenerateSoftwareInfoRequest,
  PredecessorOptions,
  SoftwareInfoChoices,
  VersionSoftwareInfo,
} from "Types";
import { apiErrorToast, filterLicenses } from "Utils";
import AiButton from "../AiButton/AiButton";
import styles from "./softwareInfo.module.scss";

type FileStreamProps = {
  isVersion: false;
};

type VersionProps = {
  predecessors?: PredecessorOptions;
  versionInputRef?: React.MutableRefObject<HTMLInputElement | null>;
  maxVersionsLimit?: number;
  isVersion: true;
};

type FileProps = {
  fileName: string;
};

type UrlImportProps = {
  importUrl: string;
};

type CommonProps = {
  offerAnalysisNotification?: boolean;
  /** `null` omits the section heading — for dialogs whose own title already says it. */
  title?: React.ReactNode | null;
};

type SoftwareInfoFormProps = (FileStreamProps | VersionProps) &
  (FileProps | UrlImportProps) &
  CommonProps;

const EMPTY_CHOICES: SoftwareInfoChoices = {
  category_options: [],
  license_options: [],
  platform_options: [],
  architecture_options_by_platform: {},
};

function SoftwareInfoForm(props: SoftwareInfoFormProps): React.JSX.Element {
  const {
    isVersion,
    offerAnalysisNotification = true,
    title = "Edit software info",
  } = props;
  const fileName = "fileName" in props ? props.fileName : undefined;
  const importUrl = "importUrl" in props ? props.importUrl : undefined;
  const predecessors = "predecessors" in props ? props.predecessors : undefined;
  const versionInputRef =
    "versionInputRef" in props ? props.versionInputRef : undefined;
  const maxVersionsLimit =
    "maxVersionsLimit" in props ? props.maxVersionsLimit : undefined;
  const isAiEnabled = useIsAiFeatureEnabled("generate_software_info_enabled");
  const [shouldShowReplace, setShouldShowReplace] = useState(false);
  const { data: choices = EMPTY_CHOICES, isSuccess: areChoicesLoaded } =
    useSoftwareInfoChoicesQuery();
  const [generateSoftwareInfo] = useGenerateSoftwareInfoMutation();
  const {
    formState: { errors },
    getValues,
    reset,
    setValue,
    trigger,
    watch,
  } = useFormContext<VersionSoftwareInfo>();
  const version = watch("version");
  const platform = watch("platform");
  const shouldReplace = watch("replace");
  const predecessorId = watch("predecessor_id");
  const existingVersion = predecessors?.find(
    (predecessor) => predecessor.softwareInfo.version === version,
  );
  const hasPredecessors = (predecessors?.length ?? 0) > 0;
  const shouldShowForce = maxVersionsLimit
    ? (predecessors?.length ?? 0) >= maxVersionsLimit
    : false;
  const oldestVersionCreatedDate = predecessors?.reduce(
    (acc, item) =>
      compareDesc(acc, item.createdDate) === 1 ? acc : item.createdDate,
    predecessors?.[0]?.createdDate,
  );
  const predecessorOptions =
    predecessors
      ?.filter((item) => {
        if (
          item.softwareInfo.version === existingVersion?.softwareInfo.version
        ) {
          return false;
        }

        if (shouldShowForce) {
          return item.createdDate !== oldestVersionCreatedDate;
        }

        return true;
      })
      .map((item) => item.id) ?? [];

  const filterLicenseOptions = (
    options: string[],
    state: FilterOptionsState<string>,
  ) => filterLicenses(options, choices.license_options, state.inputValue);

  const handleIsReleasedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = event.target.checked;
    if (checked) {
      setValue("is_released", true);
      setValue("release_date", format(new Date(), DATE_FIELD_FORMAT));
    } else {
      setValue("is_released", false);
      setValue("release_date", null);
    }
  };

  useEffect(() => {
    setShouldShowReplace(existingVersion !== undefined);
  }, [existingVersion]);

  useEffect(() => {
    if (!existingVersion) return;
    if (shouldReplace && existingVersion.predecessorId) {
      if (predecessorId !== existingVersion.predecessorId) {
        setValue("predecessor_id", existingVersion.predecessorId);
      }
    } else if (!shouldReplace && predecessorId !== null) {
      setValue("predecessor_id", null);
    }
  }, [existingVersion, predecessorId, shouldReplace, setValue]);

  const handleGenerateSoftwareInfo = async () => {
    let request: GenerateSoftwareInfoRequest;
    if (fileName) {
      request = { file_name: fileName };
    } else if (importUrl) {
      request = { import_string: importUrl };
    } else {
      return;
    }

    const progressHandle = Toast.progress({
      title: "Generating software info...",
    });

    try {
      const result = await generateSoftwareInfo(request).unwrap();

      if (!result.identified) {
        Toast.show({
          title: "Couldn't identify this software",
          description: "Fill in the software info manually.",
          type: "warning",
        });
        return;
      }

      const license = choices.license_options.find(
        (item) => item.identifier === result.license,
      );
      const currentValues = getValues();

      reset(
        {
          ...currentValues,
          product: result.product || currentValues.product,
          version: result.version || currentValues.version,
          publisher: result.publisher || currentValues.publisher,
          platform: result.platform || currentValues.platform,
          category: result.category || currentValues.category,
          architecture: result.architecture || currentValues.architecture,
          license: license ? license.license : currentValues.license,
        },
        { keepDefaultValues: true },
      );
      Toast.show({
        title: "Software info filled",
        description: "Review the fields before saving.",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not generate software info");
    } finally {
      progressHandle.dismiss();
    }
  };

  return (
    <Dialog.Section>
      {title && <Dialog.Section.Title>{title}</Dialog.Section.Title>}
      {(fileName || importUrl) && areChoicesLoaded && (
        <Dialog.Section.Action>
          <AiButton
            isAiEnabled={isAiEnabled}
            onClick={handleGenerateSoftwareInfo}
            isInline
            label="Fill"
          />
        </Dialog.Section.Action>
      )}
      <Dialog.Section.Description>
        &quot;Version&quot; may not be equal to . or .., or contain the
        following characters: {PURL_BLACKLIST_CHARS}
      </Dialog.Section.Description>
      <Dialog.Section.Content>
        <Form.FormGrid className={styles.fields}>
          <Form.TextField
            name="product"
            label="Product"
            errors={errors}
            isRequired
          />
          <Form.TextField
            id="software-info-version"
            name="version"
            label="Version"
            isRequired
            errors={errors}
            inputProps={{
              pattern: PURL_SAFE_PATTERN,
              ref: versionInputRef,
            }}
          />
          {hasPredecessors && (
            <Form.Autocomplete
              id="derived"
              label="Derived"
              isFullWidth
              name="predecessor_id"
              errors={errors}
              options={predecessorOptions}
              getOptionLabel={(option) =>
                predecessors?.find((item) => item.id === option)?.softwareInfo
                  .version ?? ""
              }
              isDisabled={
                shouldReplace &&
                existingVersion !== undefined &&
                Boolean(existingVersion.predecessorId)
              }
            />
          )}
          <Form.TextField
            name="publisher"
            isRequired
            label="Publisher"
            errors={errors}
          />
          <div className={styles["platform-row"]}>
            <Form.Autocomplete
              className={styles.platform}
              id="platform"
              isRequired
              isFullWidth
              name="platform"
              label="Platform"
              errors={errors}
              options={choices.platform_options}
              onChange={() => setValue("architecture", "Generic")}
            />
            <Form.Autocomplete
              className={styles.architecture}
              id="architecture"
              name="architecture"
              htmlInput={{
                "aria-label": "Architecture",
              }}
              errors={errors}
              options={
                choices.architecture_options_by_platform[platform] ?? [
                  "Generic",
                ]
              }
            />
          </div>
          <Form.Autocomplete
            id="category"
            name="category"
            label="Category"
            isFullWidth
            isRequired
            errors={errors}
            options={choices.category_options}
          />
          <Form.Autocomplete
            id="license"
            name="license"
            label="License"
            isFullWidth
            isRequired
            errors={errors}
            options={choices.license_options.map((item) => item.license)}
            filterOptions={filterLicenseOptions}
          />
          {isVersion && (
            <div className={styles["release-info"]}>
              <Form.Checkbox
                name="is_released"
                className={styles["is-released"]}
                label="Is released"
                onChange={handleIsReleasedChange}
              />
              <Form.DatePicker
                name="release_date"
                errors={errors}
                isRequired={watch("is_released")}
                isDisabled={!watch("is_released")}
                isFullWidth
                className={styles["release-date"]}
              />
            </div>
          )}
        </Form.FormGrid>
        <div className={styles["additional-options"]}>
          {isVersion && shouldShowForce && (
            <Form.Checkbox
              name="force"
              errors={errors}
              className={`${styles["is-released"]} ${styles.checkbox}`}
              label="Delete the oldest version if package is at maximum versions"
            />
          )}
          {isVersion && shouldShowReplace && (
            <Form.Checkbox
              name="replace"
              errors={errors}
              className={`${styles["is-released"]} ${styles.checkbox}`}
              label="Overwrite an already existing package version"
              onChange={async () => await trigger("version")}
            />
          )}
          {offerAnalysisNotification && (
            <Form.Checkbox
              name="notify_on_completion"
              errors={errors}
              className={`${styles["is-released"]} ${styles.checkbox}`}
              label={NOTIFY_ON_COMPLETION_LABEL}
            />
          )}
        </div>
      </Dialog.Section.Content>
    </Dialog.Section>
  );
}

export default SoftwareInfoForm;
